import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import {
  Truck,
  MapPin,
  Navigation,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  LogOut,
  Phone,
  Clock,
  Map,
  UserCog,
  MessageCircle,
  Package,
  ChevronRight,
  Info,
} from 'lucide-react';

import { http } from '../../lib/http';

const DriverDashboard = ({ onSwitchDashboard }) => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [routeInfo, setRouteInfo] = useState({ route_name: null, stops: [], service_date: '', next_service_date: '' });
  const [activeStopIndex, setActiveStopIndex] = useState(0);
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('Toronto');

  const fetchRoute = useCallback(async (routeOverride = null) => {
    setLoading(true);
    setError(null);
    try {
      const targetRoute = routeOverride || selectedRoute;
      const response = await http.get(`/driver/my-route?route=${encodeURIComponent(targetRoute)}`);

      const data = response.data;
      if (data.success) {
        setRouteInfo({
          route_name: data.route_name,
          stops: data.stops || [],
          driver: data.driver_identity,
          service_date: data.service_date || '',
          next_service_date: data.next_service_date || '',
        });

        const stops = data.stops || [];
        // Skip next-day-only stops (not delivering today) when finding first pending
        const firstPending = stops.findIndex(s => s.active_today && s.delivery_status !== 'Delivered');
        setActiveStopIndex(firstPending >= 0 ? firstPending : 0);

        if (user.role === 'owner' || user.role === 'admin') {
          const routesListRes = await http.get('/driver/routes');
          const routesListData = routesListRes.data;
          if (routesListData.success) {
            setAvailableRoutes(routesListData.routes || []);
          }
        }
      } else {
        setError(data.message || 'Failed to load route data.');
      }
    } catch (err) {
      console.error('Driver fetch error:', err);
      setError('Connection failed. Please check your internet.');
    } finally {
      setLoading(false);
    }
  }, [selectedRoute, user.role]);

  useEffect(() => {
    fetchRoute();
  }, [fetchRoute]);

  // Only count today's active stops for progress tracking
  const todayStops = useMemo(() =>
    routeInfo.stops.filter(s => s.active_today !== false),
    [routeInfo.stops]
  );

  const deliveredCount = useMemo(() =>
    todayStops.filter(s => s.delivery_status === 'Delivered').length,
    [todayStops]
  );

  const progressPercent = useMemo(() =>
    todayStops.length > 0 ? Math.round((deliveredCount / todayStops.length) * 100) : 0,
    [deliveredCount, todayStops.length]
  );

  const handleUpdateStatus = async (status, reason = "") => {
    const activeStop = routeInfo.stops[activeStopIndex];
    if (!activeStop) return;

    setUpdating(true);
    setError(null);
    try {
      const response = await http.post('/driver/update-status', {
        customer_id: activeStop.id,
        status: status,
        reason: reason
      });

      const data = response.data;
      if (data.success) {
        if (status === 'Delivered') {
          try {
            await http.post('/driver/confirm-delivery', { customer_id: activeStop.id });
          } catch (e) {
            console.error('Confirm delivery log error:', e);
          }
        }
        await fetchRoute();
      } else {
        setError(data.message || 'Failed to update status.');
      }
    } catch (err) {
      console.error('Status update error:', err);
      setError('Failed to sync update with server.');
    } finally {
      setUpdating(false);
    }
  };

  const handleNavigate = (address) => {
    const query = encodeURIComponent(`${address.street}, ${address.city} ${address.postal_code}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${query}`, '_blank');
  };

  const handleCall = (phoneNumber) => {
    if (!phoneNumber) return;
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWhatsApp = (stop) => {
    if (!stop?.phone) return;
    const phone = stop.phone.replace(/[\s\-()]/g, '');
    const message = encodeURIComponent(
      `Hi ${stop.name}, your order from Just Protein has been delivered. Thank you!`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  // Loading state
  if (loading && routeInfo.stops.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-950">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-400 font-medium">Loading deliveries...</p>
        </div>
      </div>
    );
  }

  const activeStop = routeInfo.stops[activeStopIndex];
  const isActiveDelivered = activeStop?.delivery_status === 'Delivered';
  const isNextDayOnly = activeStop?.active_today === false && activeStop?.active_next_day === true;
  const isTodayOnly = activeStop?.active_today === true && activeStop?.active_next_day === false;

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-6">

      {/* ── Header ── */}
      <div className="bg-gray-900 border-b border-gray-800 sticky top-0 z-20">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-orange-500 rounded-lg">
                <Truck className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-white">{routeInfo.route_name || 'No Route'}</h1>
                <p className="text-xs text-gray-400">{deliveredCount} of {todayStops.length} delivered</p>
                {routeInfo.next_service_date && (
                  <p className="text-[10px] text-orange-400/70">Prep for: {routeInfo.next_service_date}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => fetchRoute()} className="h-8 w-8 text-gray-400 hover:text-white">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              {onSwitchDashboard && (
                <Button variant="ghost" size="icon" onClick={() => onSwitchDashboard('selector')} className="h-8 w-8 text-gray-400 hover:text-orange-500">
                  <UserCog className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={logout} className="h-8 w-8 text-red-500 hover:text-red-400">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <Progress value={progressPercent} className="h-1.5 bg-gray-800" />
        </div>
      </div>

      {/* ── Owner/Admin Route Selector ── */}
      {(user.role === 'owner' || user.role === 'admin') && (
        <div className="bg-gray-900 border-b border-gray-800 px-4 py-2">
          <div className="max-w-lg mx-auto flex items-center gap-2">
            <span className="text-xs font-medium text-orange-400 whitespace-nowrap">Route:</span>
            <select
              value={selectedRoute}
              onChange={(e) => {
                setSelectedRoute(e.target.value);
                setActiveStopIndex(0);
              }}
              className="bg-gray-800 text-sm text-gray-200 py-1 px-2 rounded border border-gray-700 outline-none flex-1"
            >
              {availableRoutes.length > 0 ? (
                availableRoutes.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))
              ) : (
                <option value="Toronto">Toronto</option>
              )}
            </select>
            <Badge className="bg-orange-950/40 text-orange-400 border-orange-700 text-xs">
              Preview
            </Badge>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 pt-4 space-y-4">

        {/* ── Error Alert ── */}
        {error && (
          <Alert variant="destructive" className="bg-red-950/60 border-red-800 text-red-200">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* ── Empty State ── */}
        {routeInfo.stops.length === 0 ? (
          <div className="py-16 text-center space-y-3">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto">
              <Package className="w-8 h-8 text-gray-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-300">No deliveries assigned</h2>
            <p className="text-sm text-gray-500 px-6">
              You don't have any deliveries for today. Contact dispatch if this seems wrong.
            </p>
          </div>
        ) : (
          <>
            {/* ── Current Stop Card ── */}
            <Card className="bg-gray-900 border-gray-800 shadow-lg shadow-black/20">
              <CardContent className="p-4 space-y-4">

                {/* Status Note — delivery schedule alert */}
                {activeStop?.status_note && (
                  <div className={`p-3 rounded-lg flex items-start gap-2 ${
                    isNextDayOnly
                      ? 'bg-blue-950/40 border border-blue-800'
                      : 'bg-amber-950/40 border border-amber-800'
                  }`}>
                    <Info className={`w-5 h-5 shrink-0 mt-0.5 ${isNextDayOnly ? 'text-blue-400' : 'text-amber-400'}`} />
                    <div>
                      <p className={`text-xs font-semibold uppercase ${isNextDayOnly ? 'text-blue-400' : 'text-amber-400'}`}>
                        {isNextDayOnly ? 'Tomorrow Only' : 'Status Change'}
                      </p>
                      <p className={`text-sm font-medium ${isNextDayOnly ? 'text-blue-200' : 'text-amber-200'}`}>
                        {activeStop.status_note}
                      </p>
                    </div>
                  </div>
                )}

                {/* Stop number + Name */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500 text-white rounded-xl flex items-center justify-center text-lg font-bold shrink-0">
                    {activeStop?.stop_no}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-white truncate">{activeStop?.name}</h2>
                    <p className="text-xs text-gray-400">
                      Stop {activeStopIndex + 1} of {routeInfo.stops.length}
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div
                  className="flex items-start gap-2 cursor-pointer"
                  onClick={() => handleNavigate(activeStop?.address)}
                >
                  <MapPin className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-200">{activeStop?.address?.street}</p>
                    <p className="text-xs text-gray-500">{activeStop?.address?.city} {activeStop?.address?.postal_code}</p>
                  </div>
                </div>

                {/* Delivery Instructions */}
                {activeStop?.address?.delivery_instructions && (
                  <div className="p-3 bg-blue-950/40 border border-blue-800 rounded-lg">
                    <p className="text-xs font-medium text-blue-400 mb-1">Delivery Instructions</p>
                    <p className="text-sm text-blue-200">{activeStop.address.delivery_instructions}</p>
                  </div>
                )}

                {/* Driver Notes */}
                {/* {activeStop?.driver_notes && activeStop.driver_notes !== '-' && (
                  <div className="p-3 bg-gray-800 rounded-lg">
                    <p className="text-xs font-medium text-gray-500 mb-1">Note</p>
                    <p className="text-sm text-gray-300">{activeStop.driver_notes}</p>
                  </div>
                )} */}

                {/* Bag count */}
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs text-gray-400 border-gray-700">
                    <Package className="w-3 h-3 mr-1" />
                    {activeStop?.bag_count || 0} bags
                  </Badge>
                  {isActiveDelivered && (
                    <Badge className="bg-green-950/40 text-green-400 border-green-800 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Delivered
                    </Badge>
                  )}
                </div>

                {/* ── Action Buttons ── */}
                {isNextDayOnly ? (
                  /* Next-day-only stop: no delivery actions, just info */
                  <div className="pt-2">
                    <div className="p-4 bg-blue-950/30 border border-blue-800 rounded-xl text-center">
                      <Info className="w-6 h-6 text-blue-400 mx-auto mb-1" />
                      <p className="text-sm font-semibold text-blue-400">Tomorrow's Delivery</p>
                      <p className="text-xs text-blue-300/70 mt-1">This customer is on the next delivery run, not today's.</p>
                    </div>
                  </div>
                ) : !isActiveDelivered ? (
                  <div className="space-y-2 pt-2">
                    {/* Row 1: Navigate + Call */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        className="h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
                        onClick={() => handleNavigate(activeStop?.address)}
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        Navigate
                      </Button>
                      <Button
                        className="h-12 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl"
                        onClick={() => handleCall(activeStop?.phone)}
                        disabled={!activeStop?.phone}
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Call
                      </Button>
                    </div>

                    {/* Row 2: WhatsApp + Delivered */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        className="h-12 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl"
                        onClick={() => handleWhatsApp(activeStop)}
                        disabled={!activeStop?.phone}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp
                      </Button>
                      <Button
                        className="h-12 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl"
                        onClick={() => handleUpdateStatus('Delivered')}
                        disabled={updating}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {updating ? 'Saving...' : 'Delivered'}
                      </Button>
                    </div>

                    {/* Row 3: Not Available + Address Issue */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        className="h-10 text-orange-400 border-orange-700 hover:bg-orange-950/30 text-xs font-medium rounded-xl"
                        onClick={() => handleUpdateStatus('Not Available', 'Customer not present')}
                        disabled={updating}
                      >
                        <Clock className="w-3.5 h-3.5 mr-1" />
                        Not Available
                      </Button>
                      <Button
                        variant="outline"
                        className="h-10 text-red-400 border-red-700 hover:bg-red-950/30 text-xs font-medium rounded-xl"
                        onClick={() => handleUpdateStatus('Address Issue', 'Wrong address or access barred')}
                        disabled={updating}
                      >
                        <Map className="w-3.5 h-3.5 mr-1" />
                        Address Issue
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="pt-2 space-y-2">
                    <div className="p-3 bg-green-950/40 border border-green-800 rounded-xl text-center">
                      <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-1" />
                      <p className="text-sm font-semibold text-green-400">Delivery Complete</p>
                    </div>
                    <Button
                      className="w-full h-10 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl"
                      onClick={() => handleWhatsApp(activeStop)}
                      disabled={!activeStop?.phone}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Notify on WhatsApp
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── All Stops List ── */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
                All Stops
              </h3>
              <div className="bg-gray-900 rounded-xl border border-gray-800 divide-y divide-gray-800 overflow-hidden">
                {routeInfo.stops.map((stop, i) => {
                  const isDelivered = stop.delivery_status === 'Delivered';
                  const isCurrent = i === activeStopIndex;
                  const isFailed = stop.delivery_status === 'Not Available' || stop.delivery_status === 'Address Issue';
                  const isStopNextDayOnly = stop.active_today === false && stop.active_next_day === true;
                  const isStopTodayOnly = stop.active_today === true && stop.active_next_day === false;

                  return (
                    <div
                      key={stop.id}
                      className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors ${
                        isCurrent ? 'bg-orange-950/30 border-l-4 border-l-orange-500' : ''
                      } ${isDelivered ? 'bg-green-950/20' : ''} ${
                        isStopNextDayOnly && !isCurrent ? 'bg-blue-950/10 opacity-70' : ''
                      } ${
                        !isCurrent && !isDelivered && !isStopNextDayOnly ? 'hover:bg-gray-800/50' : ''
                      }`}
                      onClick={() => {
                        setActiveStopIndex(i);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      {/* Status icon */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${
                        isDelivered ? 'bg-green-900 text-green-400' :
                        isFailed ? 'bg-red-900 text-red-400' :
                        isStopNextDayOnly ? 'bg-blue-900 text-blue-400' :
                        isCurrent ? 'bg-orange-500 text-white' :
                        'bg-gray-800 text-gray-500'
                      }`}>
                        {isDelivered ? <CheckCircle2 className="w-4 h-4" /> :
                         isFailed ? <AlertCircle className="w-4 h-4" /> :
                         isStopNextDayOnly ? <Info className="w-4 h-4" /> :
                         stop.stop_no}
                      </div>

                      {/* Name + status */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm font-medium truncate ${
                            isDelivered ? 'text-green-400 line-through' :
                            isStopNextDayOnly ? 'text-blue-300' :
                            isCurrent ? 'text-white font-semibold' :
                            'text-gray-300'
                          }`}>
                            {stop.name}
                          </p>
                          {isStopNextDayOnly && (
                            <Badge className="bg-blue-900/60 text-blue-300 border-blue-700 text-[9px] px-1 py-0">NEW</Badge>
                          )}
                          {isStopTodayOnly && (
                            <Badge className="bg-amber-900/60 text-amber-300 border-amber-700 text-[9px] px-1 py-0">LAST DAY</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {stop.status_note ? stop.status_note :
                           isDelivered ? 'Delivered' :
                           isFailed ? stop.delivery_status :
                           stop.address?.street}
                        </p>
                      </div>

                      {isCurrent && !isDelivered && !isStopNextDayOnly && (
                        <ChevronRight className="w-4 h-4 text-orange-400 shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
